import React from 'react';

// Enhanced markdown parser with advanced formatting support
export class AdvancedMarkdownParser {
  static parseMetadata(content) {
    const metadataRegex = /^---\n([\s\S]*?)\n---\n([\s\S]*)$/;
    const match = content.match(metadataRegex);
    
    if (!match) {
      return { metadata: {}, content };
    }
    
    const [, metadataStr, bodyContent] = match;
    const metadata = {};
    
    metadataStr.split('\n').forEach(line => {
      const [key, ...valueParts] = line.split(':');
      if (key && valueParts.length > 0) {
        let value = valueParts.join(':').trim();
        
        // Remove quotes if present
        if (value.startsWith('"') && value.endsWith('"')) {
          value = value.slice(1, -1);
        }
        
        // Parse arrays
        if (value.startsWith('[') && value.endsWith(']')) {
          value = value.slice(1, -1).split(',').map(item => item.trim().replace(/"/g, ''));
        }
        
        metadata[key.trim()] = value;
      }
    });
    
    return { metadata, content: bodyContent };
  }
  
  static parseContent(content) {
    let html = content;
    
    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
    
    // Bold and Italic
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Images
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width: 100%; height: auto; border-radius: 8px; margin: 10px 0;" />');
    
    // Code blocks
    html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, language, code) => {
      return `<pre class="code-block" data-language="${language || 'text'}"><code>${this.escapeHtml(code.trim())}</code></pre>`;
    });
    
    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');
    
    // Blockquotes
    html = html.replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>');
    
    // Lists
    html = html.replace(/^\- (.*$)/gim, '<li>$1</li>');
    html = html.replace(/^(\d+)\. (.*$)/gim, '<li>$2</li>');
    
    // Wrap consecutive <li> elements in <ul> or <ol>
    html = html.replace(/(<li>.*<\/li>)/gims, (match) => {
      return `<ul>${match}</ul>`;
    });
    
    // Line breaks
    html = html.replace(/\n\n/g, '</p><p>');
    html = html.replace(/\n/g, '<br>');
    
    // Wrap in paragraphs
    html = `<p>${html}</p>`;
    
    // Clean up empty paragraphs
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p>(<h[1-6]>.*<\/h[1-6]>)<\/p>/g, '$1');
    html = html.replace(/<p>(<pre.*<\/pre>)<\/p>/g, '$1');
    html = html.replace(/<p>(<blockquote>.*<\/blockquote>)<\/p>/g, '$1');
    html = html.replace(/<p>(<ul>.*<\/ul>)<\/p>/g, '$1');
    html = html.replace(/<p>(<div.*<\/div>)<\/p>/g, '$1');
    
    // Parse custom styling (gradient text, styled divs)
    html = this.parseCustomStyling(html);
    
    return html;
  }
  
  static parseCustomStyling(html) {
    // Gradient text spans
    const gradientSpanRegex = /<span style="([^"]*gradient[^"]*)">(.*?)<\/span>/g;
    html = html.replace(gradientSpanRegex, (match, style, content) => {
      return `<span style="${style}" class="gradient-text">${content}</span>`;
    });
    
    // Styled divs
    const styledDivRegex = /<div style="([^"]*)">([\s\S]*?)<\/div>/g;
    html = html.replace(styledDivRegex, (match, style, content) => {
      return `<div style="${style}" class="styled-content">${content}</div>`;
    });
    
    // Emoji headers - simplified regex
    html = html.replace(/^(#{1,6})\s*([\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}].*)/gmu, (match, hashes, content) => {
      const level = hashes.length;
      return `<h${level} class="emoji-header">${content}</h${level}>`;
    });
    
    return html;
  }
  
  static escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  
  static createTOC(content) {
    const headers = [];
    const headerRegex = /^(#{1,6})\s*(.*)/gm;
    let match;
    
    while ((match = headerRegex.exec(content)) !== null) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      
      headers.push({
        level,
        text,
        id
      });
    }
    
    return headers;
  }
  
  static estimateReadingTime(content) {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return readingTime;
  }
}

// React component for rendering advanced markdown
export const MarkdownRenderer = ({ content, className = '' }) => {
  const { metadata, content: markdownContent } = AdvancedMarkdownParser.parseMetadata(content);
  const html = AdvancedMarkdownParser.parseContent(markdownContent);
  const readingTime = AdvancedMarkdownParser.estimateReadingTime(markdownContent);
  
  return (
    <div className={`markdown-content ${className}`}>
      {metadata.title && (
        <div className="blog-metadata">
          <h1 className="blog-title">{metadata.title}</h1>
          {metadata.description && (
            <p className="blog-description">{metadata.description}</p>
          )}
          <div className="blog-meta">
            {metadata.date && <span className="blog-date">{metadata.date}</span>}
            {metadata.author && <span className="blog-author">By {metadata.author}</span>}
            <span className="reading-time">{readingTime} min read</span>
          </div>
          {metadata.tags && (
            <div className="blog-tags">
              {metadata.tags.map(tag => (
                <span key={tag} className="blog-tag">{tag}</span>
              ))}
            </div>
          )}
        </div>
      )}
      <div 
        className="markdown-body"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

export default AdvancedMarkdownParser;